(() => {
  'use strict';
  const settings = window.tebeautyGiftSettings;
  if (!settings || window.TeBeautyGift) return;
  const root = window.Shopify?.routes?.root || '/';
  const bundleId = String(settings.bundleVariantId);
  const giftId = String(settings.giftVariantId);
  const marker = 'fade-glow';
  let queue = Promise.resolve();
  let pending = 0;
  let hasOffer = settings.hasOffer;
  let checkingOut = false;
  const isGift = (item) => String(item.variant_id) === giftId && item.properties?._tebeauty_gift === marker;
  const bundleQuantity = (cart) => cart.items.reduce((sum, item) => sum + (String(item.variant_id) === bundleId ? item.quantity : 0), 0);
  const sectionIds = (value) => Array.isArray(value) ? value : String(value || '').split(',').filter(Boolean);

  function enqueue(task) {
    pending += 1;
    const result = queue.then(task);
    queue = result.catch(() => {}).finally(() => { pending -= 1; });
    return result;
  }

  async function request(path, body) {
    const response = await fetch(root + path, body ? {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    } : { headers: { Accept: 'application/json' }, cache: 'no-store' });
    const data = await response.json();
    if (!response.ok || data.status || data.errors) {
      const error = new Error(data.description || data.message || settings.error);
      error.status = response.status;
      throw error;
    }
    return data;
  }

  async function getSections(ids) {
    if (!ids.length) return undefined;
    const url = new URL(window.location.href);
    url.searchParams.delete('section_id');
    url.searchParams.set('sections', ids.join(','));
    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) throw new Error(settings.error);
    const sections = await response.json();
    if (ids.some((id) => !sections[id])) throw new Error(settings.error);
    return sections;
  }

  function showNotice(message) {
    let notice = document.getElementById('tebeauty-gift-notice');
    if (!message) {
      if (notice) notice.remove();
      return;
    }
    if (!notice) {
      notice = document.createElement('p');
      notice.id = 'tebeauty-gift-notice';
      notice.className = 'page-width';
      notice.setAttribute('role', 'status');
      (document.getElementById('MainContent') || document.body).prepend(notice);
    }
    notice.textContent = message;
    const cartError = document.getElementById('CartDrawer-CartErrors') || document.getElementById('cart-errors');
    if (cartError) cartError.textContent = message;
  }

  async function synchronize(startCart, ids = []) {
    let cart = startCart || await request('cart.js');
    let changed = false;
    let warning = '';
    const bundles = bundleQuantity(cart);
    const manualGloves = cart.items.reduce((sum, item) => sum + (String(item.variant_id) === giftId && !isGift(item) ? item.quantity : 0), 0);
    const target = Math.max(0, bundles - manualGloves);
    let giftQuantity = cart.items.filter(isGift).reduce((sum, item) => sum + item.quantity, 0);

    while (giftQuantity > target) {
      const item = cart.items.filter(isGift).at(-1);
      const quantity = Math.max(0, item.quantity - (giftQuantity - target));
      cart = await request('cart/change.js', { id: item.key, quantity });
      changed = true;
      giftQuantity = cart.items.filter(isGift).reduce((sum, line) => sum + line.quantity, 0);
    }

    if (giftQuantity < target) {
      try {
        await request('cart/add.js', { items: [{ id: Number(giftId), quantity: target - giftQuantity, properties: { _tebeauty_gift: marker } }] });
      } catch (error) {
        if (error.status !== 422) throw error;
        warning = settings.unavailable;
      }
      cart = await request('cart.js');
      changed = true;
    }

    let paidGift = cart.items.find((item) => isGift(item) && item.final_line_price > 0);
    while (paidGift) {
      cart = await request('cart/change.js', { id: paidGift.key, quantity: 0 });
      changed = true;
      warning = settings.conflict;
      paidGift = cart.items.find((item) => isGift(item) && item.final_line_price > 0);
    }
    hasOffer = bundleQuantity(cart) > 0 || cart.items.some(isGift);
    if (changed && ids.length) cart.sections = await getSections(ids);
    return { cart, changed, warning };
  }

  async function refreshCartUI(result) {
    if (!result.changed) {
      showNotice(result.warning);
      return;
    }
    const mainItems = document.getElementById('main-cart-items');
    const mainFooter = document.getElementById('main-cart-footer');
    const sections = [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble', selector: '#cart-icon-bubble' },
    ];
    if (mainItems) sections.push({ id: mainItems.dataset.id, selector: '#main-cart-items' });
    if (mainFooter) sections.push({ id: mainFooter.dataset.id, selector: '#main-cart-footer' });
    const html = await getSections(sections.map((section) => section.id));
    sections.forEach(({ id, selector }) => {
      const target = document.querySelector(selector);
      const source = new DOMParser().parseFromString(html[id], 'text/html').querySelector(selector);
      if (target && source) target.innerHTML = source.innerHTML;
    });
    document.querySelector('cart-drawer')?.classList.toggle('is-empty', result.cart.item_count === 0);
    document.querySelector('cart-items')?.classList.toggle('is-empty', result.cart.item_count === 0);
    mainFooter?.classList.toggle('is-empty', result.cart.item_count === 0);
    showNotice(result.warning);
  }

  window.TeBeautyGift = {
    add(config) {
      return enqueue(async () => {
        const response = await fetch(window.routes.cart_add_url, config);
        const added = await response.json();
        if (!response.ok || added.status) return added;
        if (String(config.body.get('id')) === bundleId) hasOffer = true;
        const ids = sectionIds(config.body.get('sections'));
        try {
          const result = await synchronize(null, ids);
          if (result.changed) added.sections = result.cart.sections || added.sections;
          added.giftWarning = result.warning;
          showNotice(result.warning);
          return added;
        } catch (error) {
          added.giftWarning = settings.error;
          added.giftSyncFailed = true;
          showNotice(settings.error);
          return added;
        }
      });
    },
    change(body) {
      return enqueue(async () => {
        const response = await fetch(window.routes.cart_change_url, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body,
        });
        const cart = await response.json();
        if (!response.ok || cart.errors) return cart;
        const result = await synchronize(cart, sectionIds(JSON.parse(body).sections));
        showNotice(result.warning);
        return result.cart;
      });
    },
    ensure() {
      return enqueue(async () => {
        const result = await synchronize();
        await refreshCartUI(result);
        return result;
      });
    },
  };

  async function checkout(event) {
    if ((!hasOffer && !pending) || checkingOut) {
      if (checkingOut) event.preventDefault();
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    checkingOut = true;
    try {
      await window.TeBeautyGift.ensure();
      window.location.assign(root + 'checkout');
    } catch (error) {
      showNotice(settings.error);
      checkingOut = false;
    }
  }

  document.addEventListener('click', (event) => {
    const control = event.target.closest('button[name="checkout"], input[name="checkout"], a[href]');
    if (!control) return;
    if (control.tagName === 'A') {
      const url = new URL(control.href, window.location.href);
      if (url.origin !== window.location.origin || !/\/checkout\/?$/.test(url.pathname)) return;
    }
    checkout(event);
  }, true);
  document.addEventListener('submit', (event) => {
    if (event.submitter?.name === 'checkout') checkout(event);
  }, true);
  window.addEventListener('pageshow', (event) => {
    checkingOut = false;
    if (event.persisted && hasOffer) window.TeBeautyGift.ensure().catch(() => showNotice(settings.error));
  });
  if (hasOffer) window.TeBeautyGift.ensure().catch(() => showNotice(settings.error));
})();
